/**
 * DemoEntry — "Try the demo" affordance on the login screen.
 *
 * Opens a picker of the curated read-only demo tenants; selecting one mints a
 * demo session (no login) and lands the visitor inside the app. See
 * docs/platform/demo-without-login-design.md.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Sparkles, ChevronRight, UtensilsCrossed, Sofa, Code2, Ship, Building2 } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { ResponsiveDialog } from '../../../shared/components/common/ResponsiveDialog';
import { demoApi, enterDemo, type DemoVariation } from '../../../shared/auth';
import { getErrorDetail, getErrorMessage } from '../../../shared/utils/errors';

const VARIATION_ICONS: Record<string, React.ElementType> = {
  foods: UtensilsCrossed,
  furniture: Sofa,
  software: Code2,
  forwarder: Ship,
};

// The demo endpoints are hand-rolled fetch calls (unauthenticated, pre-session —
// no axios / React Query), so we translate the caught error here via the shared
// getErrorMessage() (code → friendly message, with network/timeout fallbacks)
// rather than showing one static string for every failure. We keep the raw error
// in state — not the resolved string — so the message re-translates reactively
// when the UI language changes (this component re-renders on a language switch).
function DemoError({ error }: { error: unknown }) {
  const { t } = useTranslation('auth');
  const detail = getErrorDetail(error);
  const message = getErrorMessage(error);
  // Business/system codes (e.g. DEMO_NOT_AVAILABLE) carry no reference in their
  // copy, so surface the server errorId + timestamp separately — it's what
  // correlates a stuck demo with the backend logs. Some generic codes
  // (UNKNOWN/UNEXPECTED) already embed the reference, and that varies by locale,
  // so gate on the resolved message itself: only append the line when it doesn't
  // already quote the errorId. Locale-independent, no double reference.
  const showReference =
    !!detail?.error_id && !!detail?.timestamp && !message.includes(detail.error_id);
  return (
    <div className="space-y-1">
      <p className="text-sm text-destructive">{message}</p>
      {showReference && (
        <p className="text-xs text-muted-foreground">
          {t('demo.errorReference', {
            errorId: detail.error_id,
            timestamp: detail.timestamp,
          })}
        </p>
      )}
    </div>
  );
}

export function DemoEntry({
  disabled,
  autoOpen,
  reveal,
}: {
  disabled?: boolean;
  autoOpen?: boolean;
  /**
   * `?reveal=` from the login URL — un-hides demo tenants we don't currently
   * advertise (see shared/config/demo_access.config.json `hidden`). Obscurity
   * only: those tenants hold the same synthetic, read-only data.
   */
  reveal?: string | null;
}) {
  const { t } = useTranslation('auth');
  // Marketing-site "Try the demo" deep-links to /login?demo=1, which opens the
  // picker immediately.
  const [open, setOpen] = useState(() => !!autoOpen);
  const [variations, setVariations] = useState<DemoVariation[] | null>(null);
  // Store the raw caught error (not a resolved string) so <DemoError> can
  // translate it reactively — see the note on that component above.
  const [loadError, setLoadError] = useState<unknown>(null);
  const [startingKey, setStartingKey] = useState<string | null>(null);
  const [startError, setStartError] = useState<unknown>(null);

  // Lazily fetch variations the first time the dialog opens.
  useEffect(() => {
    if (!open || variations || loadError) return;
    let cancelled = false;
    demoApi
      .getVariations(reveal)
      .then((res) => {
        if (!cancelled) setVariations(res.variations);
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoadError(error);
      });
    return () => {
      cancelled = true;
    };
  }, [open, variations, loadError, reveal]);

  const handleSelect = async (key: string) => {
    setStartError(null);
    setStartingKey(key);
    try {
      await enterDemo(key);
      // Full navigation so AuthProvider re-bootstraps from the stored demo token.
      window.location.assign('/');
    } catch (error: unknown) {
      setStartError(error);
      setStartingKey(null);
    }
  };

  const isStarting = startingKey !== null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="w-full"
        size="lg"
        disabled={disabled}
        onClick={() => setOpen(true)}
        data-testid="try-demo-button"
      >
        <Sparkles className="me-2 h-4 w-4" />
        {t('demo.tryDemo')}
      </Button>

      <ResponsiveDialog
        open={open}
        onOpenChange={(next) => {
          if (!isStarting) setOpen(next);
        }}
        title={t('demo.dialogTitle')}
        className="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('demo.dialogDescription')}</p>

          {startError != null && <DemoError error={startError} />}

          {!variations && !loadError && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {loadError != null && <DemoError error={loadError} />}

          {variations && (
            <div className="flex flex-col gap-2" data-testid="demo-variation-list">
              {variations.map((v) => {
                const Icon = VARIATION_ICONS[v.key] ?? Building2;
                const busy = startingKey === v.key;
                return (
                  <button
                    key={v.key}
                    type="button"
                    disabled={isStarting}
                    onClick={() => handleSelect(v.key)}
                    className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-start transition-colors hover:bg-accent disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid={`demo-variation-${v.key}`}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{v.name}</p>
                      <p className="truncate text-sm text-muted-foreground">{v.description}</p>
                    </div>
                    {busy ? (
                      <Loader2 className="h-5 w-5 shrink-0 animate-spin text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground rtl:rotate-180" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </ResponsiveDialog>
    </>
  );
}
